// Grep File Tool - Provides text search/grep functions for workspace files
// Extends WorkspaceTool - has access to appType and toolSystem
// Uses ReadFileTool and ListDirectoryTool internally via tool system

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class GrepFileTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'grepFileTool';
  }
  /**
   * Search for text pattern in files
   * @param {string} pattern - Text pattern to search for (supports regex if useRegex=true)
   * @param {string} path - Path to file or directory to search in
   * @param {boolean} [recursive=false] - Search recursively in directories
   * @param {boolean} [useRegex=false] - Treat pattern as regular expression
   * @param {boolean} [caseSensitive=true] - Case-sensitive search
   * @param {boolean} [includeHidden=false] - Include hidden files (starting with .)
   * @param {string[]} [fileExtensions=[]] - Filter by file extensions (empty = all files)
   * @param {number} [contextLines=0] - Number of context lines to include before/after match
   * @returns {Promise<Object>} Result with search matches
   */
  async grepFile(
    pattern,
    path,
    recursive = false,
    useRegex = false,
    caseSensitive = true,
    includeHidden = false,
    fileExtensions = [],
    contextLines = 0
  ) {
    try {
      // Validate pattern
      if (!pattern || typeof pattern !== "string") {
        return this.returnError({}, "Valid search pattern required");
      }

      // Validate path
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file or directory path required");
      }

      // Validate fileExtensions
      if (!Array.isArray(fileExtensions)) {
        return this.returnError({}, "fileExtensions must be an array");
      }

      // Validate contextLines
      if (contextLines < 0) {
        return this.returnError({}, "contextLines must be non-negative");
      }

      // Check if ReadFileTool is available
      if (!this.toolSystem?.readFileTool) {
        return this.returnError(
          {},
          "ReadFileTool is not available in the tool system"
        );
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.grepFileForBrowser(
          pattern,
          path,
          recursive,
          useRegex,
          caseSensitive,
          includeHidden,
          fileExtensions,
          contextLines
        );
      }
      if (this.appType === "local") {
        return await this.grepFileForLocal(
          pattern,
          path,
          recursive,
          useRegex,
          caseSensitive,
          includeHidden,
          fileExtensions,
          contextLines
        );
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for grepFile`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|grepfile|error",
          color1: "red",
        },
        `Failed to search: ${pattern} in ${path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Search for pattern in files for browser apps
   */
  async grepFileForBrowser(
    pattern,
    path,
    recursive,
    useRegex,
    caseSensitive,
    includeHidden,
    fileExtensions,
    contextLines
  ) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    // Normalize path
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const filesystemPath = `/files${normalizedPath}`;

    try {
      // Check if path exists
      const stats = await fs.stat(filesystemPath);
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";

      let filesToSearch = [];

      if (isDir) {
        // Get all files in directory (recursively if needed)
        filesToSearch = await this.getFilesToSearch(
          filesystemPath,
          normalizedPath,
          recursive,
          includeHidden,
          fileExtensions,
          fs
        );
      } else {
        // Single file
        if (
          this.shouldSearchFile(normalizedPath, includeHidden, fileExtensions)
        ) {
          filesToSearch = [normalizedPath];
        }
      }

      if (filesToSearch.length === 0) {
        return this.returnSuccess(
          {
            pattern,
            path: normalizedPath,
            matches: [],
            fileCount: 0,
            matchCount: 0,
          },
          "No files found to search"
        );
      }

      // Search in all files
      const allMatches = [];
      let searchedCount = 0;
      let errorCount = 0;

      for (const filePath of filesToSearch) {
        try {
          const fileMatches = await this.searchInFile(
            filePath,
            pattern,
            useRegex,
            caseSensitive,
            contextLines
          );

          if (fileMatches.length > 0) {
            allMatches.push({
              file: filePath,
              matches: fileMatches,
              matchCount: fileMatches.length,
            });
          }

          searchedCount++;
        } catch (error) {
          logger.warn(
            {
              tags: "tools|grepfile|file",
              color1: "yellow",
            },
            `Failed to search in file: ${filePath}`,
            error
          );
          errorCount++;
        }
      }

      const totalMatchCount = allMatches.reduce(
        (sum, fileResult) => sum + fileResult.matchCount,
        0
      );

      logger.log(
        {
          tags: "tools|grepfile|info",
          color1: "lime",
        },
        `Searched ${searchedCount} file(s) for pattern: ${pattern}`,
        {
          pattern,
          path: normalizedPath,
          filesSearched: searchedCount,
          filesWithMatches: allMatches.length,
          totalMatches: totalMatchCount,
          errors: errorCount,
        }
      );

      return this.returnSuccess(
        {
          pattern,
          path: normalizedPath,
          matches: allMatches,
          fileCount: searchedCount,
          matchCount: totalMatchCount,
          filesWithMatches: allMatches.length,
          errorCount,
        },
        `Search completed: ${totalMatchCount} match(es) found in ${allMatches.length} file(s)`
      );
    } catch (error) {
      if (error.code === "ENOENT") {
        return this.returnError({}, `Path not found: ${path}`);
      }
      throw error;
    }
  }

  /**
   * Grep for local apps (Revolt Desktop + workspaceLocalAppAPI).
   */
  async grepFileForLocal(
    pattern,
    path,
    recursive,
    useRegex,
    caseSensitive,
    includeHidden,
    fileExtensions,
    contextLines
  ) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.grepWorkspace) {
      return this.returnError(
        {},
        "Local grep requires Revolt Desktop (workspaceLocalAppAPI.grepWorkspace)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    let relativePath = normalizedPath.replace(/^\/+/, "");
    if (relativePath === "") {
      relativePath = ".";
    }

    const res = await api.grepWorkspace({
      rootPath: root,
      relativePath,
      pattern,
      recursive,
      useRegex,
      caseSensitive,
      includeHidden,
      fileExtensions,
      contextLines,
    });

    if (!res?.success) {
      return this.returnError({}, res?.error || "Failed to grep on disk");
    }

    const displayPath = normalizedPath;

    logger.log(
      {
        tags: "tools|grepfile|info",
        color1: "lime",
      },
      `Searched (local) ${res.fileCount} file(s) for pattern: ${pattern}`,
      {
        pattern,
        path: displayPath,
        filesSearched: res.fileCount,
        filesWithMatches: res.filesWithMatches,
        totalMatches: res.matchCount,
        errors: res.errorCount,
      }
    );

    return this.returnSuccess(
      {
        pattern,
        path: displayPath,
        matches: res.matches || [],
        fileCount: res.fileCount,
        matchCount: res.matchCount,
        filesWithMatches: res.filesWithMatches,
        errorCount: res.errorCount,
      },
      `Search completed: ${res.matchCount} match(es) found in ${res.filesWithMatches} file(s)`
    );
  }

  /**
   * Get list of files to search
   */
  async getFilesToSearch(
    filesystemPath,
    normalizedPath,
    recursive,
    includeHidden,
    fileExtensions,
    fs
  ) {
    const files = [];

    try {
      const items = await fs.readdir(filesystemPath);

      for (const item of items) {
        // Skip hidden files unless requested
        if (!includeHidden && item.startsWith(".")) {
          continue;
        }

        const itemFilesystemPath =
          filesystemPath === "/files"
            ? `/files/${item}`
            : `${filesystemPath}/${item}`;
        const itemNormalizedPath =
          normalizedPath === "/" ? `/${item}` : `${normalizedPath}/${item}`;

        try {
          const stats = await fs.stat(itemFilesystemPath);
          const isDir =
            typeof stats.isDirectory === "function"
              ? stats.isDirectory()
              : stats.type === "dir";

          if (isDir) {
            // Recursively get files from subdirectory
            if (recursive) {
              const subFiles = await this.getFilesToSearch(
                itemFilesystemPath,
                itemNormalizedPath,
                true,
                includeHidden,
                fileExtensions,
                fs
              );
              files.push(...subFiles);
            }
          } else {
            // Check if file should be searched
            if (
              this.shouldSearchFile(
                itemNormalizedPath,
                includeHidden,
                fileExtensions
              )
            ) {
              files.push(itemNormalizedPath);
            }
          }
        } catch (error) {
          // Skip files that can't be accessed
          continue;
        }
      }
    } catch (error) {
      logger.error(
        {
          tags: "tools|grepfile|readdir",
          color1: "red",
        },
        `Failed to read directory: ${filesystemPath}`,
        error
      );
    }

    return files;
  }

  /**
   * Check if a file should be searched based on filters
   */
  shouldSearchFile(filePath, includeHidden, fileExtensions) {
    const fileName = filePath.split("/").pop() || "";

    // Check hidden files
    if (!includeHidden && fileName.startsWith(".")) {
      return false;
    }

    // Check file extensions
    if (fileExtensions.length > 0) {
      const extension = this.getFileExtension(fileName);
      if (!extension || !fileExtensions.includes(extension.toLowerCase())) {
        return false;
      }
    }

    return true;
  }

  /**
   * Search for pattern in a single file
   */
  async searchInFile(filePath, pattern, useRegex, caseSensitive, contextLines) {
    // Read file with line numbers
    const readResult =
      await this.toolSystem.readFileTool.readFileWithLineNumbers(
        filePath,
        "utf8"
      );

    if (!readResult.success) {
      throw new Error(readResult.message);
    }

    const lines = readResult.data.lines;
    const matches = [];

    // Create regex pattern
    let searchPattern;
    if (useRegex) {
      try {
        searchPattern = new RegExp(pattern, caseSensitive ? "g" : "gi");
      } catch (error) {
        throw new Error(`Invalid regex pattern: ${error.message}`);
      }
    } else {
      // Escape special regex characters for literal search
      const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      searchPattern = new RegExp(escapedPattern, caseSensitive ? "g" : "gi");
    }

    // Search each line
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lineContent = line.content;
      const matchesInLine = [];

      // Find all matches in the line
      let match;
      const linePattern = new RegExp(searchPattern.source, searchPattern.flags);
      while ((match = linePattern.exec(lineContent)) !== null) {
        matchesInLine.push({
          index: match.index,
          length: match[0].length,
          text: match[0],
        });

        // Prevent infinite loop for zero-length matches
        if (match[0].length === 0) {
          linePattern.lastIndex++;
        }
      }

      if (matchesInLine.length > 0) {
        // Get context lines
        const startLine = Math.max(1, line.lineNumber - contextLines);
        const endLine = Math.min(
          readResult.data.totalLines,
          line.lineNumber + contextLines
        );

        const context = [];
        for (let j = startLine - 1; j < endLine; j++) {
          if (lines[j]) {
            context.push({
              lineNumber: lines[j].lineNumber,
              content: lines[j].content,
              isMatch: lines[j].lineNumber === line.lineNumber,
            });
          }
        }

        matches.push({
          lineNumber: line.lineNumber,
          line: lineContent,
          matches: matchesInLine,
          context: contextLines > 0 ? context : undefined,
        });
      }
    }

    return matches;
  }

  /**
   * Get file extension from filename
   */
  getFileExtension(name = "") {
    const lastDot = name.lastIndexOf(".");
    if (lastDot === -1 || lastDot === name.length - 1) {
      return "";
    }
    return name.slice(lastDot + 1).toLowerCase();
  }
}
